import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { PricingView } from '@mopla/data-models';
import { formatPriceFromCents } from '@mopla/utils';

import { BookingDetailsDropdown } from '../BookingDetailsDropdown';

import { buildLineItems } from './buildLineItems';
import { DashedDivider, PriceRow } from './styled';

export interface IBookingPriceProps {
	priceInfo: PricingView | null;
	voucherContent?: ReactNode;
	/** An applied voucher moves {voucherContent} above the total. */
	hasVoucher?: boolean;
	legLabel?: string;
	/** Previous pricing for this leg — when set, the total and individual line items
	 *  that changed are shown with their previous value struck through next to the current one. */
	previousPriceInfo?: PricingView | null;
	/** How the leg is paid, Booked ride only. */
	paymentNote?: string | null;
	testId?: string;
}

export const BookingPrice: FC<IBookingPriceProps> = ({
	priceInfo,
	voucherContent,
	hasVoucher = false,
	legLabel,
	previousPriceInfo,
	paymentNote,
	testId,
}) => {
	const { t } = useTranslation('booking');
	const [isPriceExpanded, setIsPriceExpanded] = useState(false);

	const totalPrice = Math.max(0, priceInfo?.totalCostCents ?? 0);
	const previousTotalCents = previousPriceInfo?.totalCostCents;

	const formattedLineItems = useMemo(() => {
		if (!priceInfo) return [];
		const items = buildLineItems(priceInfo, t, previousPriceInfo);
		if (priceInfo.totalCostBeforeReductionsCents != null) {
			items.push({
				label: t('summary.price.subtotal'),
				value: formatPriceFromCents(priceInfo.totalCostBeforeReductionsCents),
			});
		}
		return items;
	}, [priceInfo, t, previousPriceInfo]);

	if (!priceInfo) return null;

	const vatPercent = priceInfo.taxRate;
	const vatCents = priceInfo.totalTaxCents;
	const showVat = isPriceExpanded && vatPercent != null && vatCents != null;
	const showPaymentNote = isPriceExpanded && Boolean(paymentNote);

	return (
		<Stack gap={1.5} data-testid={testId}>
			<BookingDetailsDropdown
				title={`${t('summary.price.title')}${legLabel ? ` ${legLabel}` : ''}`}
				items={formattedLineItems}
				onExpandedChange={setIsPriceExpanded}
				toggleTestId="bookingSummary-priceDetails-button"
			/>
			<DashedDivider />

			{hasVoucher && voucherContent}

			<PriceRow>
				<Typography variant="h5-sb">{t('summary.price.total')}</Typography>
				<Stack direction="row" gap={1} alignItems="center">
					{previousTotalCents != null && previousTotalCents !== totalPrice && (
						<Typography
							variant="b1-sb"
							color="solutions.600"
							sx={{ textDecoration: 'line-through' }}
						>
							{formatPriceFromCents(previousTotalCents)}
						</Typography>
					)}
					<Typography variant="b1-sb" data-testid="booking-totalPrice-block">
						{formatPriceFromCents(totalPrice)}
					</Typography>
				</Stack>
			</PriceRow>

			{(showVat || showPaymentNote) && (
				<Stack gap={1}>
					{showVat && (
						<PriceRow>
							<Typography variant="b2" data-testid="booking-vat-block">
								{t('summary.price.vat', { percent: vatPercent })}
							</Typography>
							<Typography variant="b2">
								{formatPriceFromCents(Math.max(0, vatCents))}
							</Typography>
						</PriceRow>
					)}

					{showPaymentNote && (
						<PriceRow>
							<Typography variant="b2" data-testid="booking-paymentNote-block">
								{paymentNote}
							</Typography>
						</PriceRow>
					)}
				</Stack>
			)}

			{!hasVoucher && voucherContent}
		</Stack>
	);
};
